import { Component } from '@theme/component';

const VERT_SRC = `#version 300 es
layout(location=0) in vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uPulseColor;
uniform vec2 uPulseOrigin;
uniform float uPulseAge;
uniform vec3 uJuiceColor;
out vec4 outColor;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

/* Three octaves of the noise above, each doubling frequency and halving
   amplitude — turns a single noise field into the kind of layered, oily
   turbulence real liquid actually has instead of one uniform ripple size. */
float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v += amp * snoise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return v;
}

/* Finite, asymmetric drape band: gaussian falloff across the band width,
   noise-perturbed centerline and width so no two bands read identically.
   The wobble/width noise samples p (raw rotated PIXEL coordinates, easily
   into the thousands) scaled down to a proper low frequency before feeding
   simplex noise — the earlier version fed p.y in almost unscaled (*0.9,
   *0.6), which is far too high a frequency for smooth variation and
   aliased into a fine diagonal cross-hatch instead of a soft wobble. */
float drapeBand(vec2 p, float centerline, float length_, float width, float wobbleSeed) {
  float wobble = snoise(vec2(p.y * 0.003, wobbleSeed)) * 40.0;
  float dx = p.x - (centerline + wobble);
  float widthNoise = 0.75 + 0.5 * snoise(vec2(p.y * 0.002, wobbleSeed + 11.0));
  float w = width * widthNoise;
  float across = exp(-(dx * dx) / (2.0 * w * w));

  // The finite lit segment doesn't just sit in the middle of its axis and
  // jitter — it slowly travels the whole length of that axis and wraps, so
  // over time the band roams anywhere along the diagonal rather than being
  // pinned to one stretch of it (a real light shaft moving through liquid,
  // not a fixed window with noise inside it).
  float alongSpeed = 5.0 + 4.0 * snoise(vec2(wobbleSeed, 21.7));
  float roamRange = uResolution.y * 3.0;
  float center = mod(uTime * alongSpeed + wobbleSeed * 97.0, roamRange) - roamRange * 0.5;
  float half_ = length_ * 0.32;
  float along = smoothstep(center - half_, center - half_ * 0.3, p.y)
              * (1.0 - smoothstep(center + half_ * 0.3, center + half_, p.y));

  return across * along;
}

void main() {
  vec2 pixelPos = gl_FragCoord.xy;
  pixelPos.y = uResolution.y - pixelPos.y;
  vec2 flowUv = pixelPos / uResolution.y;

  /* Breathing pulse: two off-frequency sines so the cycle never repeats identically. */
  float breathe = sin(uTime * 0.22) * 0.5 + sin(uTime * 0.071 + 1.7) * 0.5;
  breathe = smoothstep(-1.0, 1.0, breathe);

  /* Perfume juice: the fragrance's own color (uJuiceColor, sampled from its
     bottle) stretched into a body of liquid via Beer-Lambert-style
     absorption below — lighter near the "surface" (top), darker and more
     saturated with depth (bottom), always a true shade of uJuiceColor
     itself (see the comment on the base color below for why, not a mix
     toward a second hue). Depth stays a clean top-to-bottom read (plus the slow
     breathing pulse) rather than noise-driven — letting the fine
     turbulence darken it too produced uneven blotchy patches instead of a
     liquid gradient. The two noise scales below (swell, ripple) still
     drive the surface highlight, just not this base shading. */
  vec3 juiceLight = mix(uJuiceColor, vec3(1.0, 0.97, 0.9), 0.03);
  float swell = fbm(flowUv * 1.1 + vec2(uTime * 0.016, -uTime * 0.011));
  float ripple = fbm(flowUv * 3.6 + vec2(-uTime * 0.05, uTime * 0.037));
  float depth = clamp(flowUv.y * 0.62 + breathe * 0.12, 0.0, 1.0);

  /* Beer-Lambert absorption, not a mix toward black or a second hue: real
     liquid gets darker AND more saturated with depth/thickness because it
     absorbs light of its own color, it doesn't shift color. Scaling
     brightness down (mix toward 0.16x) then pushing saturation back up
     around that same darker value — instead of a flat black/white mix —
     keeps every pixel a true shade of uJuiceColor, just deeper. */
  vec3 darkened = juiceLight * mix(0.62, 0.24, depth);
  float luma = dot(darkened, vec3(0.299, 0.587, 0.114));
  vec3 base = mix(vec3(luma), darkened, 1.0 + depth * 0.22);

  /* Drape folds: fbm stretched hard along one axis (0.4x frequency) and
     compressed across it (2.6x) reads as elongated vertical folds sliding
     past each other — the same silhouette real draped fabric or a poured
     liquid's surface has — rather than the round blobs an isotropic noise
     field would give. Small, purely additive amplitude: texture on top of
     the existing depth gradient, not a second competing shading pass. */
  float foldAngle = radians(12.0);
  vec2 foldDir = vec2(cos(foldAngle), sin(foldAngle));
  vec2 foldAxis = vec2(flowUv.x * foldDir.x - flowUv.y * foldDir.y, flowUv.x * foldDir.y + flowUv.y * foldDir.x);
  float drapeFold = fbm(vec2(foldAxis.x * 2.6, foldAxis.y * 0.4) + vec2(uTime * 0.02, uTime * 0.012));
  base += juiceLight * drapeFold * 0.1;

  /* A soft, drifting top-down highlight — light glinting off a moving
     liquid surface rather than an evenly lit flat color — its center
     wandering slowly on its own noise so it never sits still. */
  float aspect = uResolution.x / uResolution.y;
  vec2 lightPos = vec2(aspect * 0.5 + swell * 0.22, 0.22 + ripple * 0.1);
  float lightDist = distance(flowUv, lightPos);
  float surfaceGlow = exp(-lightDist * lightDist * 2.2);
  base += juiceLight * surfaceGlow * 0.28;

  /* Caustics: thin, bright, flowing bands of light — the shimmer real
     liquid throws on itself as its surface bends light unevenly. This was
     pulled once on suspicion of causing the diagonal cross-hatch artifact,
     but that was a misdiagnosis — it samples fbm(flowUv * ...), normalized
     UV space, the same safe scale as swell/ripple above; the actual bug was
     drapeBand()'s wobble noise sampling raw pixel coordinates almost
     unscaled (since fixed). Safe to bring back. Tinted toward the juice
     color rather than pure white so the sparkle reads as light IN the
     liquid, not a layer floating above it. */
  float causticField = fbm(flowUv * 2.2 + vec2(uTime * 0.05, uTime * 0.033));
  float caustic = pow(abs(sin(causticField * 7.0 + uTime * 0.4)), 9.0);
  base += mix(vec3(1.0, 0.94, 0.85), juiceLight, 0.4) * caustic * 0.16;

  /* Diagonal drape: angled steep-vertical, direction right-to-left — reads
     as a shaft of light refracting through the liquid (warm-white,
     additive over the colored moving base) rather than fabric. */
  float angle = radians(169.0);
  vec2 dir = vec2(cos(angle), sin(angle));
  vec2 axisPos = vec2(
    pixelPos.x * dir.y - pixelPos.y * dir.x,
    pixelPos.x * dir.x + pixelPos.y * dir.y
  );

  float warp = snoise(vec2(axisPos.y * 0.0035, uTime * 0.02)) * 0.1 * uResolution.x;
  axisPos.x += warp;

  float bandGlow = 0.0;
  float period = uResolution.x * 0.42;
  for (int i = 0; i <= 3; i++) {
    float seed = float(i) * 7.0;
    // drift: each band's own slow, continuous slide along the diagonal
    // axis — a real sweep over time, not just a static position with a
    // slow wobble riding on top of it (which read as "not moving").
    float driftSpeed = 14.0 + 8.0 * snoise(vec2(seed, 9.9));
    float centerline = (float(i) + 0.5) * period + snoise(vec2(seed, 3.1)) * period * 0.3 + uTime * driftSpeed;
    centerline = mod(centerline, period * 4.0) - period * 1.5; // wrap so a drifting band loops back in from the far side
    float bandWidth = 46.0 + 20.0 * snoise(vec2(seed, 8.4));
    float bandLength = uResolution.y * (0.32 + 0.14 * snoise(vec2(seed, 2.2)));
    bandGlow += drapeBand(axisPos, centerline, bandLength, bandWidth, seed) * 0.6;
  }
  // Contrast alone couldn't take this low enough: overlapping bands sum
  // unbounded, so a bright crossing point stayed visible no matter how
  // small the multiplier got. Clamping the sum first gives contrast a
  // fixed ceiling to work from, so it actually controls the visible max.
  bandGlow = clamp(bandGlow, 0.0, 1.0);

  float bandContrast = 0.05 + 0.03 * breathe;
  vec3 color = base + vec3(1.0, 0.97, 0.9) * bandGlow * bandContrast;

  /* Bubbles: a fixed cast of ~14, each a deterministic pseudo-random seed
     (no per-instance uniforms/buffers — cheap enough to loop per-pixel),
     rising at its own slow speed and looping once it drifts off the top,
     with a gentle horizontal sway so the field never marches in lockstep.
     Each is a soft filled disc plus a brighter thin rim (real bubbles are
     dim in the middle and catch the light at their edge) and a small
     offset highlight dot standing in for a specular glint — plus a faint
     inner shadow right at the center, since a real bubble's surface bends
     light AWAY from its center rather than just sitting flat and bright;
     without it a bubble reads as a painted disc instead of a lensed sphere. */
  float bubbleGlow = 0.0;
  float bubbleShadow = 0.0;
  for (int i = 0; i < 14; i++) {
    float fi = float(i);
    float speed = 26.0 + 34.0 * fract(sin(fi * 57.31) * 4321.7);
    float xSeed = fract(sin(fi * 91.73) * 10000.3);
    float phase = fract(sin(fi * 13.11) * 999.1);
    float bx = xSeed * uResolution.x + sin(uTime * 0.16 + fi * 2.3) * 16.0;
    float by = mod((1.0 - phase) * (uResolution.y + 220.0) - uTime * speed, uResolution.y + 220.0) - 110.0;
    vec2 bp = vec2(bx, by);
    float r = 2.6 + 4.0 * fract(sin(fi * 33.37) * 555.9);

    float d = distance(pixelPos, bp);
    float fill = (1.0 - smoothstep(0.0, r, d)) * 0.1;
    float rim = smoothstep(r * 0.72, r, d) * (1.0 - smoothstep(r, r * 1.18, d));
    vec2 glintOffset = vec2(-r * 0.32, -r * 0.32);
    float glint = 1.0 - smoothstep(0.0, r * 0.32, distance(pixelPos, bp + glintOffset));
    float innerShadow = 1.0 - smoothstep(0.0, r * 0.45, d);

    bubbleGlow += fill + rim * 0.55 + glint * 0.4;
    bubbleShadow += innerShadow;
  }
  color += vec3(1.0, 0.98, 0.94) * clamp(bubbleGlow, 0.0, 1.0) * 0.5;
  color *= 1.0 - clamp(bubbleShadow, 0.0, 1.0) * 0.1;

  /* Diffusion glow: flipping a card no longer tints the whole screen
     uniformly — a soft, wide bloom spreads outward from that card's own
     screen position and fades as it travels, the way a scent (or a drop of
     color in water) actually diffuses from its source, rather than an
     ambient effect disconnected from what the user just touched. Kept
     deliberately quiet — a wide, low-contrast bloom in each tier's own
     already-matched pulse color, not a bright defined ring, so it reads as
     an ambient shift rather than an effect calling attention to itself. */
  float pulseDist = distance(pixelPos, uPulseOrigin * uResolution) / uResolution.y;
  float waveRadius = uPulseAge * 0.75;
  float waveWidth = 0.4 + uPulseAge * 0.1;
  float ring = exp(-pow(pulseDist - waveRadius, 2.0) / (2.0 * waveWidth * waveWidth));
  float pulseFade = 1.0 - clamp(uPulseAge, 0.0, 1.0);
  // Deliberately faint — this should register as a quiet mood shift, not a
  // visible effect; if a viewer can point at it, it's too strong.
  color = mix(color, uPulseColor, ring * pulseFade * 0.12);

  outColor = vec4(color, 1.0);
}`;

function hexToRgb01(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || '').trim());
  if (!m) return [0.722, 0.447, 0.118]; // amber perfume-juice fallback
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
}

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile failed: ${info}`);
  }
  return shader;
}

/**
 * Ambient WebGL background: a body of the fragrance's own "juice" color
 * (data-juice-color, sampled from the bottle), turbulent with layered noise,
 * a diagonal light shaft refracting through it, and a rising field of soft
 * bubbles. Flipping a note card sends a
 * soft diffusion wave outward from that card's own screen position — not a
 * flat whole-screen tint — so the background reads as reacting to what the
 * user just touched rather than looping independently of it. Pauses its
 * render loop outside the viewport and under prefers-reduced-motion.
 * @extends {Component}
 */
class FragrancePyramidComponent extends Component {
  requiredRefs = ['canvas'];

  connectedCallback() {
    super.connectedCallback();

    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._lastWidth = 0;
    this._lastHeight = 0;
    this._raf = null;
    this._start = performance.now();
    this._pulseStart = null;
    this._pulseColor = [0, 0, 0];
    this._pulseOrigin = [0.5, 0.5];
    this._juiceColor = hexToRgb01(this.dataset.juiceColor);

    this.addEventListener('fragrance:note-flip', (event) => {
      this._pulseStart = performance.now();
      this._pulseColor = event.detail.color;

      const cardEl = event.target;
      const canvasRect = this.refs.canvas.getBoundingClientRect();
      if (cardEl?.getBoundingClientRect && canvasRect.width > 0 && canvasRect.height > 0) {
        const cardRect = cardEl.getBoundingClientRect();
        this._pulseOrigin = [
          (cardRect.left + cardRect.width / 2 - canvasRect.left) / canvasRect.width,
          (cardRect.top + cardRect.height / 2 - canvasRect.top) / canvasRect.height,
        ];
      }
      // Water mode: the card flip sends one large ring across the surface.
      this._water?.drop(this._pulseOrigin[0], this._pulseOrigin[1], 0.12, 0.09);
    });

    // Defer shader compile/link and the first resize+render until this
    // section is actually about to scroll into view. Running it
    // unconditionally at connect time meant the GL setup cost landed during
    // the page's very first seconds regardless of scroll position — most
    // visitors haven't scrolled anywhere near this section yet at that
    // point, so that cost was pure, avoidable main-thread contention with
    // whatever else the page was trying to do to become interactive. A
    // generous rootMargin means it's already running well before it's
    // actually on screen, so nothing about the reveal itself changes.
    this._initObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._initObserver.disconnect();
            this._startHeavyInit();
          }
        }
      },
      { rootMargin: '600px 0px' }
    );
    this._initObserver.observe(this);
  }

  async _startHeavyInit() {
    // Optional water background (data-bg="water" + data-water-src). Falls back to the liquid
    // shader below when the module fails to load or the browser has no float render targets.
    if (this.dataset.bg === 'water' && this.dataset.waterSrc) {
      try {
        const mod = await import(this.dataset.waterSrc);
        this._water = mod.createWaterBackground(this.refs.canvas, {
          juice: this._juiceColor,
          coarse: window.matchMedia('(pointer: coarse)').matches,
        });
      } catch (error) {
        this._water = null;
      }
    }
    if (!this._water) this._initGL();
    else this._bindWaterPointer();

    if (this._reducedMotion) {
      this.classList.add('fragrance-pyramid--visible');
    } else {
      this._revealObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.classList.add('fragrance-pyramid--visible');
              this._revealObserver?.disconnect();
            }
          }
        },
        { threshold: 0.15 }
      );
      this._revealObserver.observe(this);
    }

    if (!this._gl && !this._water) return;

    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._startLoop();
            this._playWaterEntrance();
          } else {
            this._stopLoop();
          }
        }
      },
      { threshold: 0.12 }
    );
    this._visibilityObserver.observe(this);

    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this);
    this._resize();

    if (this._reducedMotion) {
      this._render(0);
    } else {
      this._startLoop();
    }
  }

  /** When the section scrolls into view the water answers with a slow set of ripples, so it reads as real liquid. */
  _playWaterEntrance() {
    if (!this._water || this._reducedMotion) return;
    const now = performance.now();
    if (this._lastEntrance && now - this._lastEntrance < 9000) return;
    this._lastEntrance = now;
    const rect = this.refs.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const tiers = [...this.querySelectorAll('.fragrance-pyramid__tier')];
    const points = tiers.map((tier) => {
      const r = tier.getBoundingClientRect();
      return [(r.left + r.width / 2 - rect.left) / rect.width, (r.top + r.height / 2 - rect.top) / rect.height];
    });
    const seq = [[200, 0.5, 0.5, 0.17, 0.05]];
    points.forEach((p, i) => seq.push([900 + i * 600, p[0], p[1], 0.09, 0.05]));
    seq.push([2900, 0.16, 0.4, 0.06, 0.045], [3500, 0.84, 0.62, 0.06, 0.045]);
    this._water.sequence(seq);
  }

  _bindWaterPointer() {
    let prev = null;
    const toUv = (event) => {
      const rect = this.refs.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return [(event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height];
    };
    this._onWaterMove = (event) => {
      const p = toUv(event);
      if (!p || p[0] < 0 || p[0] > 1 || p[1] < 0 || p[1] > 1) return;
      this._water?.brush(p[0], p[1], prev ? prev[0] : null, prev ? prev[1] : null);
      prev = p;
    };
    this._onWaterLeave = () => {
      prev = null;
    };
    this.addEventListener('pointermove', this._onWaterMove, { passive: true });
    this.addEventListener('pointerleave', this._onWaterLeave, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._onWaterMove) {
      this.removeEventListener('pointermove', this._onWaterMove);
      this.removeEventListener('pointerleave', this._onWaterLeave);
    }
    this._water?.dispose();
    this._stopLoop();
    this._initObserver?.disconnect();
    this._revealObserver?.disconnect();
    this._visibilityObserver?.disconnect();
    this._resizeObserver?.disconnect();
  }

  _initGL() {
    const canvas = this.refs.canvas;
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false });
    if (!gl) return;
    this._gl = gl;

    const program = gl.createProgram();
    gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERT_SRC));
    gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`);
    }
    this._program = program;

    const quad = new Float32Array([-1, -1, 3, -1, -1, 3]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this._uResolution = gl.getUniformLocation(program, 'uResolution');
    this._uTime = gl.getUniformLocation(program, 'uTime');
    this._uPulseColor = gl.getUniformLocation(program, 'uPulseColor');
    this._uPulseOrigin = gl.getUniformLocation(program, 'uPulseOrigin');
    this._uPulseAge = gl.getUniformLocation(program, 'uPulseAge');
    this._uJuiceColor = gl.getUniformLocation(program, 'uJuiceColor');
    gl.useProgram(program);
  }

  _resize() {
    const canvas = this.refs.canvas;
    const rect = this.getBoundingClientRect();
    // Soft liquid caustics have no fine detail, so render at 1x and let CSS upscale the canvas:
    // a full-screen fragment shader at 2x DPR was the main cost while scrolling the pyramid.
    const dpr = this._water && window.matchMedia('(pointer: coarse)').matches ? 0.6 : this._water ? 0.85 : 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));

    if (width === this._lastWidth && height === this._lastHeight) return;
    this._lastWidth = width;
    this._lastHeight = height;

    canvas.width = width;
    canvas.height = height;
    if (this._water) {
      this._water.resize(width, height);
      if (this._reducedMotion) this._water.render(performance.now(), true);
      return;
    }
    this._gl.viewport(0, 0, width, height);
    if (this._reducedMotion) this._render(performance.now());
  }

  _startLoop() {
    if (this._raf || this._reducedMotion) return;
    const loop = (now) => {
      this._render(now);
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  _stopLoop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }

  _render(now) {
    if (this._water) {
      this._water.render(now || performance.now(), this._reducedMotion);
      return;
    }
    const gl = this._gl;
    if (!gl) return;

    let pulseAge = 1; // 1 = fully faded / no wave visible
    if (this._pulseStart !== null) {
      const elapsed = (now || performance.now()) - this._pulseStart;
      pulseAge = Math.min(1, elapsed / 1600);
    }

    gl.uniform2f(this._uResolution, this._lastWidth, this._lastHeight);
    gl.uniform1f(this._uTime, (now - this._start) / 1000);
    gl.uniform3f(this._uPulseColor, this._pulseColor[0], this._pulseColor[1], this._pulseColor[2]);
    gl.uniform2f(this._uPulseOrigin, this._pulseOrigin[0], this._pulseOrigin[1]);
    gl.uniform1f(this._uPulseAge, pulseAge);
    gl.uniform3f(this._uJuiceColor, this._juiceColor[0], this._juiceColor[1], this._juiceColor[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}

if (!customElements.get('fragrance-pyramid-component')) {
  customElements.define('fragrance-pyramid-component', FragrancePyramidComponent);
}
